import { Add as AddIcon } from "@mui/icons-material";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  useTheme,
} from "@mui/material";
import { filterNoUndefinedNoNull } from "@nvon/baseline";
import { important } from "@nvon/react-toolbox";
import { replaceHtmlWithComponent } from "nwon-html-to-react";
import { useCallback, useMemo, useState } from "react";
import { mapGenericContent } from "../../helpers/htmlToComponentMappings/landingpageMappings/htmlTagsMappedToReactComponents";
import { SLHtmlFromLangFile } from "../sections/home/SLHtmlFromLangFile";

import { RVOSchemaFAQPage } from "../third-party/SchemaOrg/RVOSchemaFAQPage";
import {
  getSchemaAnswer,
  getSchemaQuestion,
} from "../third-party/SchemaOrg/schemaHelper";
import { FaqQuestion } from "./RVOFaqTreeInterfaces";

const accordionSpacing = 2;

interface RVOFaqComponentProps {
  pageId?: string;
  faqQuestions: FaqQuestion[];
  smallTitles?: boolean;
}

const RVOFaqComponent = (
  props: RVOFaqComponentProps,
): React.JSX.Element | null => {
  const { smallTitles } = props;
  const theme = useTheme();
  const [expanded, setExpanded] = useState<string | null>(null);

  const handleChange = useCallback(
    (panel: string) => (event: unknown, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : null);
    },
    [],
  );

  const faqStructuredDataMainEntities = useMemo(() => {
    return props.faqQuestions
      .map((question) => {
        const questionFields = question?.questionFields;
        if (
          !question.id ||
          !questionFields ||
          !questionFields.answer ||
          typeof questionFields.answer !== "string" ||
          !questionFields.question
        ) {
          return undefined;
        }

        const answer = getSchemaAnswer({ text: questionFields.answer });
        const questionStructured = getSchemaQuestion({
          name: questionFields.question,
          acceptedAnswer: answer,
        });

        return questionStructured;
      })
      .filter(filterNoUndefinedNoNull);
  }, [props.faqQuestions]);

  return (
    <>
      <RVOSchemaFAQPage questions={faqStructuredDataMainEntities} />

      <Box component="section" aria-label="Häufig gestellte Fragen">
        {props.faqQuestions.map((question, index) => {
          const questionFields = question?.questionFields;
          const questionId = question?.id;
          return (
            <Accordion
              expanded={expanded === questionId}
              onChange={handleChange(questionId || "")}
              key={questionId}
              component={"div"}
              sx={{
                margin: "0 auto !important",
                backgroundColor: "transparent",
                boxShadow: "none",
                "&.MuiAccordion-rounded:nth-of-type(1):before": {
                  backgroundColor: "transparent",
                },
                "&.MuiAccordion-rounded:first-of-type": {
                  borderTopLeftRadius: "8px",
                  borderTopRightRadius: "8px",
                },
                "&.MuiAccordion-rounded:last-child": {
                  border: "none",
                },
                "&.Mui-expanded": {
                  backgroundColor: `${theme.palette.warning.main} !important`,
                  borderBottom: "0px !important",
                  borderRadius: "8px !important",
                },
              }}
            >
              <AccordionSummary
                expandIcon={
                  expanded === questionId ? (
                    <RemoveIcon
                      sx={{
                        color: theme.palette.background.default,
                        fontSize: theme.spacing(4),
                        backgroundColor: theme.palette.primary.main,
                        borderRadius: "50%",
                        width: important(theme.spacing(4.5)),
                        height: important(theme.spacing(4.5)),
                        padding: theme.spacing(0.5),
                      }}
                    />
                  ) : (
                    <AddIcon
                      sx={{
                        color: theme.palette.background.default,
                        fontSize: theme.spacing(4),
                        backgroundColor: theme.palette.primary.main,
                        borderRadius: "50%",
                        width: important(theme.spacing(4.5)),
                        height: important(theme.spacing(4.5)),
                        padding: theme.spacing(0.5),
                      }}
                    />
                  )
                }
                aria-controls={`faq-content-${questionId}`}
                id={`faq-header-${questionId}`}
                sx={{
                  padding: theme.spacing(accordionSpacing),

                  "&.Mui-expanded": {
                    margin: "12px 0",
                    pb: theme.spacing(accordionSpacing / 2),
                  },
                  [theme.breakpoints.down("sm")]: {},
                }}
              >
                {/* 
                  Using component="span" to avoid invalid HTML nesting.
                  MUI Accordion already wraps AccordionSummary in a heading element,
                  so we use span here while keeping h3 visual styling via variant.
                */}
                <Typography
                  sx={{
                    fontWeight: smallTitles
                      ? theme.typography.h5.fontWeight
                      : theme.typography.h4.fontWeight,
                    color: theme.palette.text.primary,

                    marginBottom: 0,
                    [theme.breakpoints.down("sm")]: {
                      marginLeft: 0,
                      paddingTop: theme.spacing(3),
                    },
                  }}
                  variant={smallTitles ? "h5" : "h3"}
                  component="span"
                >
                  <SLHtmlFromLangFile
                    htmlString={`${index + 1}.  ${questionFields?.question ?? ""}`}
                  />
                </Typography>
              </AccordionSummary>

              <AccordionDetails
                id={`faq-content-${questionId}`}
                sx={{
                  padding: theme.spacing(accordionSpacing),
                  paddingTop: 0,

                  [theme.breakpoints.up("lg")]: {
                    maxWidth: "75%",
                  },
                  [theme.breakpoints.down("sm")]: {
                    padding: theme.spacing(1, 0, 2, 0),
                  },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    marginLeft: theme.spacing(4),
                    marginRight: theme.spacing(4),
                    color: theme.palette.primary.dark,
                    [theme.breakpoints.down("md")]: {},
                    [theme.breakpoints.down("sm")]: {},
                  }}
                  component="div"
                >
                  {typeof questionFields?.answer === "string"
                    ? replaceHtmlWithComponent({
                        html: questionFields?.answer || "",
                        mapFromTagsToComponents: mapGenericContent,
                      })
                    : questionFields?.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </>
  );
};

export default RVOFaqComponent;
